"use client";

import React from "react";

import { useState, useContext } from "react";
import axios from "axios";
import { base_url } from "../../config";
import { Check, Calendar, Plus, X, Search, Info } from "lucide-react";
import WhiteBoxLayout from "../../components/WhiteBoxLayout/WhiteBoxLayout";
import RegistrationContext from "../../contexts/RegistrationContext";

// First, define the dates arrays with locations and prices
const workshopDates = [
  {
    date: "15",
    month: "Aug",
    day: "Sat",
    year: "2026",
    location: "Bremen",
    price: 150,
  },
  {
    date: "16",
    month: "Aug",
    day: "Sun",
    year: "2026",
    location: "Bremen",
    price: 150,
  },
  {
    date: "17",
    month: "Aug",
    day: "Mon",
    year: "2026",
    location: "Bremen",
    price: 150,
  },
];

const tutorialDates = [
  {
    date: "15",
    month: "Aug",
    day: "Sat",
    year: "2026",
    location: "Bremen",
    price: 120,
  },
  {
    date: "16",
    month: "Aug",
    day: "Sun",
    year: "2026",
    location: "Bremen",
    price: 120,
  },
  {
    date: "17",
    month: "Aug",
    day: "Mon",
    year: "2026",
    location: "Bremen",
    price: 120,
  },
];


const getWorkshopDate = (workshop: any): string => {
  const dayMap: Record<string, string> = {
    day1: "Aug 15th",
    day2: "Aug 16th",
    day3: "Aug 17th",
  };

  const result: string[] = [];

  for (let i = 1; i <= 3; i++) {
    const morning = workshop[`day${i}_morning`];
    const afternoon = workshop[`day${i}_afternoon`];
    const date = dayMap[`day${i}`];

    if (morning && afternoon) {
      result.push(`${date} full day`);
    } else if (morning) {
      result.push(`${date} AM`);
    } else if (afternoon) {
      result.push(`${date} PM`);
    }
  }

  return result.join(", ");
};


const getTutorialDate = (tutorial: any): string => {
  const dayMap: Record<string, string> = {
    day1: 'Aug 15th',
    day2: 'Aug 16th',
    day3: 'Aug 17th',
  };

  const parts: string[] = [];

  for (let i = 1; i <= 3; i++) {
    const dayKey = `day${i}`;
    const date = dayMap[dayKey];

    const slots = [
      { time: 'morning', label: 'morning' },
      { time: 'afternoon', label: 'afternoon' },
    ];

    for (const { time, label } of slots) {
      const firstHalf = tutorial[`${dayKey}_${time}_1st`] as number;
      const secondHalf = tutorial[`${dayKey}_${time}_2nd`] as number;

      if (firstHalf && secondHalf) {
        parts.push(`${date} ${label}`);
      } else if (firstHalf) {
        parts.push(`${date} ${label} (before coffee break)`);
      } else if (secondHalf) {
        parts.push(`${date} ${label} (after coffee break)`);
      }
    }
  }

  return parts.join(', ');
};


// Simple Modal Component
function Modal({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">{children}</div>
      </div>
    </div>
  );
}

export default function WorkshopSelection() {
  //const [selectedWorkshopDates, setSelectedWorkshopDates] = useState<number[]>( []  );
  //const [selectedTutorialDates, setSelectedTutorialDates] = useState<number[]>( [] );
  //const [selectedTutorials, setSelectedTutorials] = useState<number[]>([]);


  const [isWorkshopModalOpen, setIsWorkshopModalOpen] = useState(false);
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  const [searchWorkshop, setSearchWorkshop] = useState("");
  const [searchTutorial, setSearchTutorial] = useState("");
  const {
    setError,
    reload,
    setReload,
    workshops,
    tutorials,
    data,
    cart,
    setCart,
    optionAccess,
  } = useContext(RegistrationContext);
  const [selectedWorkshopDates, setSelectedWorkshopDates] = useState<number[]>(
    () => {
      const init: number[] = [];
      if (data.selection.workshop_day1) init.push(0);
      if (data.selection.workshop_day2) init.push(1);
      if (data.selection.workshop_day3 === 1) init.push(2);
      return init;
    }

  );

  const [selectedTutorialDates, setSelectedTutorialDates] = useState<number[]>(
    () => {
      const init: number[] = [];
      if (data.selection.tutorial_day1) init.push(0);
      if (data.selection.tutorial_day2) init.push(1);
      if (data.selection.tutorial_day3) init.push(2);
      return init;
    }
  );

  const [selectedWorkshops, setSelectedWorkshops] = useState<number[]>(data.selected_ws);
  const [selectedTutorials, setSelectedTutorials] = useState<number[]>(data.selected_tut);

  const [, setTotalPrice] = useState(cart.total_price);

  const handleSelect = async () => {
    // Reset login error state

    setError("");
    try {
      // Make a POST request to your authentication endpoint
      await axios.post(`${base_url}/api/progress`, {});
      setReload(reload + 1);
    } catch (error: any) {
      setError("Unexpected error. " + error.response.data.message);
    }
  };

  // Toggle workshop date selection
  const toggleWorkshopDate = async (index: number) => {
    const response = await axios.post(`${base_url}/api/update_ws`, {
      type: "ws",
      id: index,
      selected: !selectedWorkshopDates.includes(index),
    });

    setTotalPrice(response.data.cart.total_price);
    setCart(response.data.cart);
    setSelectedWorkshopDates((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
    
  };

  // Toggle tutorial date selection
  const toggleTutorialDate = async (index: number) => {
    const response = await axios.post(`${base_url}/api/update_ws`, {
      type: "tut",
      id: index,
      selected: !selectedTutorialDates.includes(index),
    });

    setTotalPrice(response.data.cart.total_price);
    setCart(response.data.cart);
    setSelectedTutorialDates((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Toggle workshop selection
  const toggleWorkshop = (id: number) => {

    const action = selectedWorkshops.includes(id) ? "remove" : "add";
    axios.post(`${base_url}/api/update_ws_sel`, {
      type: "ws",
      id: id,
      action: action,
    });
    setSelectedWorkshops((prev) =>
      prev.includes(id) ? prev.filter((wId) => wId !== id) : [...prev, id]
    );
  };

  // Toggle tutorial selection
  const toggleTutorial = (id: number) => {
    const action = selectedTutorials.includes(id) ? "remove" : "add";
    axios.post(`${base_url}/api/update_ws_sel`, {
      type: "tut",
      id: id,
      action: action,
    });
    setSelectedTutorials((prev) =>
      prev.includes(id) ? prev.filter((tId) => tId !== id) : [...prev, id]
    );
  };

  // Workshops with at least one scheduled slot
  const scheduledWorkshops = workshops.filter((w: any) =>
    [1, 2, 3, 4].some(
      (i) => w[`day${i}_morning`] || w[`day${i}_afternoon`]
    )
  );

  // Filter workshops based on search (only those with a confirmed date)
  const filteredWorkshops = scheduledWorkshops.filter(
    (workshop: any) =>
      workshop.title.toLowerCase().includes(searchWorkshop.toLowerCase()) ||
      (workshop.description || "")
        .toLowerCase()
        .includes(searchWorkshop.toLowerCase()) ||
      workshop.workshop_code
        .toLowerCase()
        .includes(searchWorkshop.toLowerCase())
  );

  // Filter tutorials based on search
  const filteredTutorials = tutorials.filter(
    (tutorial) =>
      tutorial.title.toLowerCase().includes(searchTutorial.toLowerCase()) ||
      tutorial.description.toLowerCase().includes(searchTutorial.toLowerCase()) ||
      tutorial.tutorial_code
        .toLowerCase()
        .includes(searchTutorial.toLowerCase())
  );



  return (
    <>
      <WhiteBoxLayout title="Conditions" handleSelect={handleSelect}>
        <h1 className="text-xl font-bold mb-4">
          Workshops & Tutorials: Register by Day
        </h1>

        {/* Registration Model Information */}
        <div className="rounded-md border-l-4 border-amber-500 bg-amber-50 p-4 mb-4 flex items-start shadow-sm">
          <Info className="h-5 w-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />

          <div className="text-sm text-amber-900 space-y-2">
            <p>
              <b>Important:</b> Registration is per <b>day of attendance</b>,
              not for specific workshops or tutorials. Select the days you plan
              to attend, and you'll have access to every workshop and tutorial
              scheduled on those days.
            </p>
            <p>
              Only workshops with a confirmed date are shown in the list below.
              The full programme is still being finalised &mdash; you can
              register now and, once the remaining workshops are scheduled,
              update your day selection from your registration page after
              payment.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Workshop Dates */}
          <div>
            <h2 className="text-base font-semibold mb-2">
              Select Workshop Dates
            </h2>
            <div className="grid grid-cols-3 gap-2">
              {workshopDates.map((date, index) => (
                <button
                  key={index}
                  onClick={() => toggleWorkshopDate(index)}
                  className={`relative flex flex-col items-center justify-center p-2 rounded-md border transition-colors ${selectedWorkshopDates.includes(index)
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-200 hover:border-blue-300"
                    }`}
                >
                  {selectedWorkshopDates.includes(index) && (
                    <div className="absolute top-1 right-1 bg-blue-600 rounded-full p-0.5">
                      <Check className="h-2 w-2 text-white" />
                    </div>
                  )}
                  <div className="flex items-center justify-center">
                    <Calendar className="h-3 w-3 text-gray-500 mr-1" />
                    <span className="text-xs text-gray-500">
                      {date.day}
                    </span>
                  </div>
                  <span className="text-lg font-bold">{date.date}</span>
                  <span className="text-xs font-medium">{date.month}</span>
                  <span className="text-xs text-blue-600 mt-1">
                    {date.location}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Tutorial Dates */}
          <div>
            <h2 className="text-base font-semibold mb-2">
              Select Tutorial Dates {optionAccess==="full" && "(included in full registration)"}
            </h2>
            <div className="grid grid-cols-3 gap-2">
              {tutorialDates.map((date, index) => (
                <button
                  key={index}
                  onClick={() => toggleTutorialDate(index)}
                  className={`relative flex flex-col items-center justify-center p-2 rounded-md border transition-colors ${selectedTutorialDates.includes(index)
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-200 hover:border-blue-300"
                    }`}
                >
                  {selectedTutorialDates.includes(index) && (
                    <div className="absolute top-1 right-1 bg-blue-600 rounded-full p-0.5">
                      <Check className="h-2 w-2 text-white" />
                    </div>
                  )}
                  <div className="flex items-center justify-center">
                    <Calendar className="h-3 w-3 text-gray-500 mr-1" />
                    <span className="text-xs text-gray-500">
                      {date.day}
                    </span>
                  </div>
                  <span className="text-lg font-bold">{date.date}</span>
                  <span className="text-xs font-medium">{date.month}</span>
                  <span className="text-xs text-blue-600 mt-1">
                    {date.location}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <h1 className="text-xl font-bold mt-10 mb-4">
          Workshops & Tutorials: Let Us Know Your Preferences
        </h1>
        {/* Information Box */}
        <div className=" rounded-md p-3 mb-4 flex items-start">
          <Info className="h-5 w-5 text-sky-500 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-sky-800">
            <p className="font-medium mb-1">
              Why we ask for your workshop and tutorial preferences:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>To help us allocate rooms appropriately</li>
              <li>To inform organizers about expected attendance</li>
            </ul>
            <p className="mt-2 ">
              We appreciate your realistic selection to help us prepare for
              the event better.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Selected Workshops */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-base font-semibold">Selected Workshops</h2>
              <button
                onClick={() => setIsWorkshopModalOpen(true)}
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium disabled:text-gray-200"
                disabled={selectedWorkshopDates.length === 0}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add
              </button>
            </div>

            {selectedWorkshops.length === 0 ? (
              <div className="p-2 border border-dashed rounded-md text-center text-gray-500 text-sm">
                No workshops selected
              </div>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                {selectedWorkshops.map((id) => {
                  const workshop = workshops.find((w) => w.id === id);
                  if (!workshop) return null;

                  return (
                    <div
                      key={id}
                      className="p-2 rounded-md border border-blue-600 bg-blue-50 text-sm"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1 mr-2">
                          <h3 className="font-medium">
                            <b>{workshop.workshop_code}</b>: {workshop.title}
                          </h3>
                          <div className="mt-1">
                            {getWorkshopDate(workshop) ? (
                              <span className="text-xs text-blue-700">
                                Set for: {getWorkshopDate(workshop)}
                              </span>
                            ) : (
                              <span className="text-xs italic text-gray-500">
                                Not yet scheduled
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleWorkshop(id);

                          }}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Tutorials */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-base font-semibold">Selected Tutorials</h2>
              <button
                onClick={() => setIsTutorialModalOpen(true)}
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add
              </button>
            </div>

            {selectedTutorials.length === 0 ? (
              <div className="p-2 border border-dashed rounded-md text-center text-gray-500 text-sm">
                No tutorials selected
              </div>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                {selectedTutorials.map((id) => {
                  const tutorial = tutorials.find((t) => t.id === id);
                  if (!tutorial) return null;

                  return (
                    <div
                      key={id}
                      className="p-2 rounded-md border border-blue-600 bg-blue-50 text-sm"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1 mr-2">
                          <h3 className="font-medium"><b>{tutorial.tutorial_code}</b>:  {tutorial.title}</h3>
                          <div className="mt-1">
                            <span className="text-xs text-blue-700">
                              Set for: {getTutorialDate(tutorial)}
                              {/* Set for: {tutorial.dates.join(", ")} */}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTutorial(id);
                          }}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Price Summary
          <div className="border-t pt-4 mt-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-base font-semibold">Total Price</h2>
                <p className="text-xs text-gray-600">Based on selected dates</p>
              </div>
              <div className="text-xl font-bold">${totalPrice}</div>
            </div>
          </div> */}

        {/* Workshop Modal */}
        <Modal
          isOpen={isWorkshopModalOpen}
          onClose={() => setIsWorkshopModalOpen(false)}
          title="Available Workshops"
        >
          <div className="relative mb-3">
            <input
              type="text"
              placeholder="Search workshops..."
              className="w-full pl-8 pr-2 py-1.5 border rounded-md text-sm"
              value={searchWorkshop}
              onChange={(e) => setSearchWorkshop(e.target.value)}
            />
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {filteredWorkshops.filter((workshop) => {
              const isday1 = workshop.day1_morning || workshop.day1_afternoon;
              const isday2 = workshop.day2_morning || workshop.day2_afternoon;
              const isday3 = workshop.day3_morning || workshop.day3_afternoon;
              if (!isday1 && !isday2 && !isday3) return true;
              if (selectedWorkshopDates.includes(0) && isday1) return true;
              if (selectedWorkshopDates.includes(1) && isday2) return true;
              if (selectedWorkshopDates.includes(2) && isday3) return true;
              return false;
            })
              .map((workshop) => (
                <div
                  key={workshop.id}
                  onClick={() => toggleWorkshop(workshop.id)}
                  className={`p-2 rounded-md border cursor-pointer transition-colors text-sm ${selectedWorkshops.includes(workshop.id)
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-200 hover:border-blue-300"
                    }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1 mr-2">
                      <h3 className="font-medium">
                        <b>{workshop.workshop_code}</b>: {workshop.title}
                      </h3>
                      <p className="text-xs text-gray-600">
                        {workshop.description}
                      </p>
                      <div className="mt-1 text-xs">
                        {getWorkshopDate(workshop) ? (
                          <span className="text-blue-700">Set for: {getWorkshopDate(workshop)}</span>
                        ) : (
                          <span className="italic text-gray-500">Not yet scheduled</span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`h-4 w-4 rounded-full border flex items-center justify-center ${selectedWorkshops.includes(workshop.id)
                        ? "bg-blue-600 border-blue-600"
                        : "border-gray-300"
                        }`}
                    >
                      {selectedWorkshops.includes(workshop.id) && (
                        <Check className="h-2 w-2 text-white" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            {filteredWorkshops.length === 0 && (
              <div className="p-2 text-center text-gray-500 text-sm">
                No workshops found matching your search.
              </div>
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => setIsWorkshopModalOpen(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-4 rounded text-sm"
            >
              Done
            </button>
          </div>
        </Modal>

        {/* Tutorial Modal */}
        <Modal
          isOpen={isTutorialModalOpen}
          onClose={() => setIsTutorialModalOpen(false)}
          title="Available Tutorials"
        >
          <div className="relative mb-3">
            <input
              type="text"
              placeholder="Search tutorials..."
              className="w-full pl-8 pr-2 py-1.5 border rounded-md text-sm"
              value={searchTutorial}
              onChange={(e) => setSearchTutorial(e.target.value)}
            />
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {filteredTutorials.filter((tutorial) => {
              const isday1 = tutorial.day1_morning_1st || tutorial.day1_morning_2nd || tutorial.day1_afternoon_1st || tutorial.day1_afternoon_2nd;
              const isday2 = tutorial.day2_morning_1st || tutorial.day2_morning_2nd || tutorial.day2_afternoon_1st || tutorial.day2_afternoon_2nd;
              const isday3 = tutorial.day3_morning_1st || tutorial.day3_morning_2nd || tutorial.day3_afternoon_1st || tutorial.day3_afternoon_2nd;
              if (selectedTutorialDates.includes(0) && isday1) return true;
              if (selectedTutorialDates.includes(1) && isday2) return true;
              if (selectedTutorialDates.includes(2) && isday3) return true;
              return false;
            })
              .map((tutorial: any) => (
              <div
                key={tutorial.id}
                onClick={() => toggleTutorial(tutorial.id)}
                className={`p-2 rounded-md border cursor-pointer transition-colors text-sm ${selectedTutorials.includes(tutorial.id)
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-200 hover:border-blue-300"
                  }`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1 mr-2">
                    <h3 className="font-medium"><b>{tutorial.tutorial_code}:</b> {tutorial.title}</h3>
                    <p className="text-xs text-gray-600">
                      {tutorial.description}
                    </p>
                    <div className="mt-1 text-xs text-blue-700">
                      Set for: {getTutorialDate(tutorial)}
                      {/* Set for: {tutorial.dates.join(", ")} */}
                    </div>
                  </div>
                  <div
                    className={`h-4 w-4 rounded-full border flex items-center justify-center ${selectedTutorials.includes(tutorial.id)
                      ? "bg-blue-600 border-blue-600"
                      : "border-gray-300"
                      }`}
                  >
                    {selectedTutorials.includes(tutorial.id) && (
                      <Check className="h-2 w-2 text-white" />
                    )}
                  </div>
                </div>
              </div>
            ))}
            {filteredTutorials.length === 0 && (
              <div className="p-2 text-center text-gray-500 text-sm">
                No tutorials found matching your search.
              </div>
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => setIsTutorialModalOpen(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-4 rounded text-sm"
            >
              Done
            </button>
          </div>
        </Modal>

      </WhiteBoxLayout>

    </>
  );
}
